<template>
  <v-container
    v-if="!payments?.length && Object.keys(usePaymentStore().filterPaymentCondition).length === 0"
  >
    <v-skeleton-loader v-if="paymentsLoading" class="mx-auto border mt-2" type="table" />
    <create-payment-card v-if="!paymentsLoading" />
  </v-container>

  <v-row v-else>
    <v-col sm="12" md="12">
      <payment-filter-bar />

      <v-card v-if="usePaymentStore().noResults" align="center" class="pa-3">
        {{ $t("page.payment.filter.noResultsFound") }}</v-card
      >
      <v-card v-else>
        <base-stripe-data-table
          :headers="headers"
          searchAction="getFilteredPayments"
          itemsGetter="payments"
          storeNamespace="payment"
          :actionLoading="paymentsLoading"
          :pagination-scope="JSON.stringify(condition)"
        >
          <template v-slot:item="{ item }">
            <tr :key="item.id" @click="goTo(asPayment(item))" style="cursor: pointer">
              <td class="text-center">
                <div class="payment-amount">
                  <span v-if="getPaymentDisplayAmount(asPayment(item)) === undefined">{{
                    $t("page.paymentDetails.authorization.unavailable")
                  }}</span>
                  <span v-else v-money="{ amount: getPaymentDisplayAmount(asPayment(item)) }" />
                  <span class="payment-currency">
                    {{ asPayment(item).currency?.toUpperCase() }}
                  </span>
                </div>
              </td>
              <td class="text-center">
                <status-chip
                  :status="usePaymentStore().getPaymentStatus(asPayment(item)) ?? undefined"
                  :capture-before="getPaymentCaptureBefore(asPayment(item))"
                  :partiallyRefundAmount="getPartiallyRefundAmount(asPayment(item))"
                  :errorMessage="getErrorMessage(asPayment(item)) ?? undefined"
                  :cart="asPayment(item)?.metadata?.isCart === 'true'"
                  :payment-id="asPayment(item)?.id"
                  :intent-status="asPayment(item)?.status ?? undefined"
                />
              </td>
              <td class="text-center">
                <p
                  v-if="!isPaymentLink(asPayment(item))"
                  class="pr-8 text-grey"
                  v-dash="asPayment(item).description"
                />
                <p v-else class="pr-8 text-grey" v-dash="paymentLinkName(asPayment(item))" />
              </td>
              <td class="text-center">
                <p class="text-medium-emphasis" v-dash="formatPaymentCard(asPayment(item))" />
              </td>
              <td class="text-center">
                <p class="text-medium-emphasis" v-dateWithTime="asPayment(item).created" />
              </td>
              <td class="text-center">
                <div class="pr-6">
                  {{
                    isPaymentLink(asPayment(item))
                      ? $t("common.action.paymentLink")
                      : locationName(asPayment(item))
                  }}
                  <v-tooltip :text="getHintMessage(asPayment(item))" location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="xs"
                        color="grey"
                        class="pl-3"
                        v-if="deletedCheck(asPayment(item))"
                        >mdi-information</v-icon
                      >
                    </template></v-tooltip
                  >
                  <div class="text-medium-emphasis text-body-2">
                    {{
                      isPaymentLink(asPayment(item))
                        ? formatString(
                            asPayment(item)?.paymentLink?.name ||
                              asPayment(item)?.paymentLink?.id ||
                              "",
                          )
                        : terminalName(asPayment(item))
                    }}
                  </div>
                </div>
              </td>
              <td class="text-center">
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon flat>
                      <v-icon color="grey">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(action, index) in getPaymentActions(asPayment(item))"
                      :key="index"
                      :value="index"
                      @click.stop="action.action(asPayment(item).id)"
                    >
                      <v-list-item-title>{{ action.title }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </base-stripe-data-table>
      </v-card>
    </v-col>
  </v-row>
  <refund-dialog ref="refundDialog" @updated="handleRefundUpdated" />
</template>

<script lang="ts" setup>
import { usePaymentStore } from "@/payments/stores/payments";
import { ref, computed, watch } from "vue";
import i18n from "@/app/plugins/i18n";
import StatusChip from "@/shared/components/Common/StatusChip.vue";
import PaymentFilterBar from "@/payments/components/payment/PaymentFilterBar.vue";
import CreatePaymentCard from "@/dashboard/components/dashboard/CreatePaymentCard.vue";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import RefundDialog from "@/shared/components/Common/RefundDialog.vue";
import { useRouter, RouteLocationRaw } from "vue-router";
import { PaymentStatus } from "@/shared/enums/status.enum";
import BaseStripeDataTable from "@/shared/components/Common/BaseStripeDataTable.vue";
import { useAuthStore } from "@/auth/stores/auth";
import { formatString } from "@/shared/utils/firstLast4.utils";
import { useCartStore } from "@/cart/stores/cart";
import { Payment, NamedEntityRef } from "@/payments/types/payments";
import { formatPaymentCard } from "@/payments/utils/paymentCard.utils";

import {
  getPaymentCaptureBefore,
  getPaymentDisplayAmount,
} from "@/payments/utils/paymentStatus.utils";

const asPayment = (row: { id?: string }): Payment => row as Payment;

const refundDialog = ref<null | { open: (id?: string) => null }>(null);
const payments = computed(() => usePaymentStore().payments);
const condition = computed(() => usePaymentStore().filterPaymentCondition);

const router = useRouter();
const paymentsLoading = computed(() => usePaymentStore().listLoading || usePaymentStore().loading);
const isPaymentLink = (payment: Payment) => {
  return payment.metadata?.payment_link !== undefined;
};

const paymentLinkName = (payment: Payment) => {
  if (payment.paymentLink?.name) {
    return formatString(payment.paymentLink.name);
  }
};

const entityRef = (value: NamedEntityRef | string | null | undefined): NamedEntityRef | null =>
  value && typeof value === "object" ? value : null;

const locationName = (payment: Payment) => entityRef(payment.location)?.name;
const terminalName = (payment: Payment) => entityRef(payment.terminal)?.name;

watch(
  () => ({
    clearedFilter: usePaymentStore().clearedFilter,
    condition: condition.value,
  }),
  async ({ clearedFilter }) => {
    if (clearedFilter) {
      const paymentStore = usePaymentStore();
      paymentStore.setClearAllFilter(false);
      await paymentStore.getFilteredPayments();
    } else {
      await usePaymentStore().getFilteredPayments({
        params: { limit: 10 },
      });
    }
  },
);

interface PaymentTableHeader {
  title: string;
  align: "start" | "center" | "end";
  key: string;
  sortable: boolean;
}

const headers: PaymentTableHeader[] = [
  {
    title: i18n.global.t("page.payment.amount"),
    align: "center",
    key: "amount",
    sortable: false,
  },

  {
    title: i18n.global.t("common.action.status"),
    align: "center",
    key: "status",
    sortable: false,
  },
  {
    title: i18n.global.t("common.action.description"),
    align: "center",
    key: "description",
    sortable: false,
  },
  {
    title: i18n.global.t("page.payment.cardSummary"),
    align: "center",
    key: "card",
    sortable: false,
  },
  {
    title: i18n.global.t("page.dashboard.payments.date"),
    align: "center",
    key: "created",
    sortable: false,
  },
  {
    title: i18n.global.t("common.action.source"),
    align: "center",
    key: "terminal",
    sortable: false,
  },
  {
    title: "",
    align: "end",
    key: "menu",
    sortable: false,
  },
];

interface PaymentRowAction {
  title: string;
  action: (id: string) => void | Promise<void>;
}

const ACTIONS = {
  VIEW_DETAILS: (id: string): PaymentRowAction => ({
    title: i18n.global.t("page.dashboard.payments.viewDetails"),
    action: () => viewDetails(id),
  }),
  REFUND: (id: string): PaymentRowAction => ({
    title: i18n.global.t("page.dashboard.payments.refund"),
    action: async (paymentId: string) => {
      // First retrieve the payment and set it as the selected payment
      // This ensures the payment is available in the store for the refund dialog
      const paymentStore = usePaymentStore();
      const payment = await paymentStore.retrievePayment(paymentId);
      if (payment) {
        paymentStore.setSelectedPayment(payment);
        // Now open the refund dialog with the payment ID
        refundDialog.value?.open(paymentId);
      }
    },
  }),
  SEND_TO_READER: (id: string): PaymentRowAction => ({
    title: i18n.global.t("page.dashboard.payments.sendToReader"),
    action: (paymentId: string) => sendToReaderById(paymentId),
  }),
  ADD_TO_CART: (id: string): PaymentRowAction => ({
    title: i18n.global.t("common.cart.copyCart"),
    action: (paymentId: string) => {
      void useCartStore().copyCart(paymentId);
    },
  }),
  CANCEL_PAYMENT: (id: string): PaymentRowAction => ({
    title: i18n.global.t("page.dashboard.payments.cancelPayment"),
    action: async (paymentId: string) => {
      const paymentStore = usePaymentStore();
      await paymentStore.cancelPayment(paymentId);
    },
  }),
};

// Helper function to conditionally add cart action
const addCartActionIfEligible = (actions: PaymentRowAction[], payment: Payment) => {
  if (payment.metadata?.isCart) {
    actions.push(ACTIONS.ADD_TO_CART(payment.id));
  }
  return actions;
};

// dynamically add actions based on payment status
const getPaymentActions = (payment: Payment) => {
  const status = usePaymentStore().getPaymentStatus(payment);
  const isStaff = useAuthStore().isStaff;

  // Add cancel payment action for requires_payment_method or requires_capture status
  if (
    status === PaymentStatus.REQUIRES_PAYMENT_METHOD ||
    status === PaymentStatus.REQUIRES_CONFIRMATION
  ) {
    return addCartActionIfEligible(
      [ACTIONS.CANCEL_PAYMENT(payment.id), ACTIONS.VIEW_DETAILS(payment.id)],
      payment,
    );
  }

  // Handle refunded or canceled payments
  if (status === PaymentStatus.REFUNDED || status === PaymentStatus.CANCELED) {
    return addCartActionIfEligible([ACTIONS.VIEW_DETAILS(payment.id)], payment);
  }

  // Handle failed payments
  if (payment.status !== PaymentStatus.SUCCEEDED) {
    return addCartActionIfEligible([ACTIONS.SEND_TO_READER(payment.id)], payment);
  }

  // Handle successful payments
  const actions = isStaff
    ? [ACTIONS.VIEW_DETAILS(payment.id)]
    : [ACTIONS.REFUND(payment.id), ACTIONS.VIEW_DETAILS(payment.id)];

  return addCartActionIfEligible(actions, payment);
};

const getPartiallyRefundAmount = (payment: Payment) => {
  const latestCharge =
    payment?.latest_charge && typeof payment.latest_charge === "object"
      ? payment.latest_charge
      : null;
  if (
    latestCharge &&
    (latestCharge.amount_refunded ?? 0) > 0 &&
    (latestCharge.amount_refunded ?? 0) < payment.amount
  ) {
    return (latestCharge.amount_refunded ?? 0) / 100;
  }
  return 0;
};

const getErrorMessage = (payment: Payment) => {
  const lastPaymentError = payment?.last_payment_error;
  if (lastPaymentError && typeof lastPaymentError === "object" && lastPaymentError.code) {
    return lastPaymentError.code.replace(/_/g, " ");
  }
  return null;
};

const customerQueryValue = (customer: unknown) => {
  return customer !== null && typeof customer === "object"
    ? (customer as { id?: string }).id
    : (customer as string | null | undefined);
};

const getHintMessage = (payment: Payment) => {
  const bothDeleted = entityRef(payment.location)?.deleted && entityRef(payment.terminal)?.deleted;
  if (bothDeleted) {
    return i18n.global.t("page.payment.deletedHint.locationAndReader");
  } else if (entityRef(payment.terminal)?.deleted) {
    return i18n.global.t("page.payment.deletedHint.reader");
  } else if (entityRef(payment.location)?.deleted) {
    return i18n.global.t("page.payment.deletedHint.location");
  }
};

const deletedCheck = (payment: Payment) => {
  return entityRef(payment.terminal)?.deleted || entityRef(payment.location)?.deleted;
};

const sendToReaderById = (id: string) => {
  router.push({
    name: "processPayment",
    params: { id },
    query: { recover: "true" },
  });
};

const sendToReader = (payment: Payment) => {
  const routeParams: RouteLocationRaw = payment.id
    ? {
        name: "processPayment",
        params: { id: payment.id },
        query: {
          customer: customerQueryValue(payment?.customer) ?? undefined,
          recover: "true",
        },
      }
    : { name: "createPayment" };

  router.push(routeParams);
};

const viewDetails = (id: string) => {
  router.push({ path: `/payments/details/${id}` });
};

const goTo = (payment: Payment) => {
  if (payment.status === PaymentStatus.CANCELED) {
    viewDetails(payment.id);
    return;
  }

  if (
    payment.status === PaymentStatus.SUCCEEDED ||
    payment.status === PaymentStatus.REQUIRES_CAPTURE
  ) {
    viewDetails(payment.id);
    return;
  }

  sendToReader(payment);
};

const handleRefundUpdated = async () => {
  // Get the current selected payment ID before refreshing
  const paymentId = usePaymentStore().selectedPayment?.id;

  // Refresh the payments list to reflect the updated payment
  await usePaymentStore().getFilteredPayments({
    params: { limit: 10 },
  });

  // If we have a payment ID, retrieve the updated payment and update it in the store
  if (paymentId) {
    const updatedPayment = await usePaymentStore().retrievePayment(paymentId);
    if (updatedPayment) {
      usePaymentStore().setSelectedPayment(updatedPayment);
    }
  }
};
</script>

<style scoped>
.payment-amount {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}

.payment-currency {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.875rem;
}
</style>
